import useRolesPermissions from "hooks/useRolesPermissions";
import { useMemo } from "react";
import { useStyles } from "./RCAMeetingSetting.styles";
import { Alert, IconButton, List, ListItem, ListItemText, Snackbar } from "@mui/material";
import { useTranslation } from "react-i18next";
import { handleNameTranslation } from "utilities/localesUtilities";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { RCAMeetingDeletionModal } from "./components/RCAMeetingDeletionModal/RCAMeetingDeletionModal";
import { MeetingTextfieldForm } from "./components/MeetingTextfieldForm/MeetingTextfieldForm";
import { useCreateEditMeetingActions } from "./hooks/useCreateEditMeetingActions";

export const RCAMeetingTypesSetting = () => {
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const { RCA_PERMISSIONS } = useRolesPermissions();
    const userCanEdit = useMemo(() => RCA_PERMISSIONS?.settings?.canEdit, [RCA_PERMISSIONS]);
    const {
        handleAddMeetingTypeSubmit,
        setSnackbarErrorMessage,
        setSnackbarMessage,
        refetch,
        setDeleteMeeting,
        setTypeToEdit,
        getInitFormValues,
        snackbarErrorMessage,
        snackbarMessage,
        defaultTypes,
        customTypes,
        languageIdSelected,
        typeToEdit,
        deleteMeeting,
    } = useCreateEditMeetingActions();

    return (
        <div>
            <div className={classes.formSubtitle}>{t("rca.settings.meeting_settings.default_meetings")}</div>

            <List style={{ marginBottom: "24px" }} className={classes.listContainer}>
                {defaultTypes.map((type) => {
                    const nameTranslation = handleNameTranslation(
                        i18n.language,
                        type?.default_translation?.default_meeting_translation_names as {
                            name: string;
                            language_id: number;
                        }[],
                    );
                    return (
                        <ListItem key={type.id} className={classes.keySuccessFactorlistItem}>
                            <ListItemText className={classes.listText} primary={nameTranslation} />
                        </ListItem>
                    );
                })}
            </List>

            <div className={classes.formSubtitle}>{t("rca.settings.meeting_settings.custom_meetings")}</div>
            <MeetingTextfieldForm
                initFormValues={getInitFormValues()}
                onSubmit={handleAddMeetingTypeSubmit}
                onKeyDown
            />

            <List style={{ marginBottom: "24px" }} className={classes.listCustomContainer}>
                {customTypes.map((type) => {
                    const isTranslationInSelectedLanguage = type?.translation?.translation_names.find(
                        (tn) => tn.language_id === languageIdSelected,
                    );
                    const nameTranslation = handleNameTranslation(
                        i18n.language,
                        type?.translation?.translation_names as {
                            name: string;
                            language_id: number;
                        }[],
                    );

                    return (
                        <ListItem key={type.id} className={classes.keySuccessFactorlistItem}>
                            {typeToEdit && typeToEdit.id === type.id ? (
                                <MeetingTextfieldForm
                                    initFormValues={getInitFormValues(
                                        isTranslationInSelectedLanguage ? nameTranslation : "",
                                    )}
                                    placeholder={t("rca.settings.meeting_settings.textfield_placeholder_translation")}
                                    onSubmit={handleAddMeetingTypeSubmit}
                                    setTypeToEdit={setTypeToEdit}
                                />
                            ) : (
                                <>
                                    <ListItemText className={classes.listText} primary={nameTranslation} />
                                    <IconButton
                                        disabled={!userCanEdit}
                                        onClick={() => {
                                            setTypeToEdit({
                                                id: type.id,
                                                translation: type.translation,
                                            });
                                        }}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        disabled={!userCanEdit}
                                        onClick={() => {
                                            setDeleteMeeting({
                                                id: type.id,
                                                translation: type.translation,
                                            });
                                        }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </>
                            )}
                        </ListItem>
                    );
                })}
            </List>
            {!!deleteMeeting && (
                <RCAMeetingDeletionModal
                    deleteMeeting={deleteMeeting}
                    setDeleteMeeting={setDeleteMeeting}
                    refetch={refetch}
                />
            )}
            <Snackbar
                open={!!snackbarMessage || !!snackbarErrorMessage}
                onClose={() => {
                    setSnackbarMessage("");
                    setSnackbarErrorMessage("");
                }}
                autoHideDuration={6000}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Alert severity={snackbarErrorMessage ? "error" : "success"} sx={{ width: "100%" }}>
                    {snackbarMessage || snackbarErrorMessage}
                </Alert>
            </Snackbar>
        </div>
    );
};
